import Link from "next/link"
import { ArrowRight, Snowflake, Sun } from "lucide-react"
import type { GuideMetadata } from "@/data/guide-slugs"
import type { GrassCharacteristics } from "@/lib/types/recommendation-engine"
import { GRASS_BLADES } from "./grass-blades"
import { getSeasonTheme } from "./season-theme"

interface GuideLandingCardProps {
  metadata: GuideMetadata
  grassData: GrassCharacteristics
}

function maintenanceLabel(value: string | undefined): string {
  if (!value) return "Medium"
  const lower = value.toLowerCase()
  if (lower.includes("very low")) return "Very low"
  if (lower.includes("very high")) return "Very high"
  if (lower.includes("low")) return "Low"
  if (lower.includes("high")) return "High"
  return "Medium"
}

export function GuideLandingCard({
  metadata,
  grassData,
}: GuideLandingCardProps) {
  const isCool = metadata.season === "cool"
  const Blade = GRASS_BLADES[metadata.slug]
  const { blade: bladeColor } = getSeasonTheme(metadata.season)

  const headerGradient = isCool
    ? "bg-linear-to-b from-emerald-50/80 to-transparent"
    : "bg-linear-to-b from-amber-50/80 to-transparent"

  const pillStyles = isCool
    ? "bg-white/85 border-emerald-200 text-emerald-800"
    : "bg-white/85 border-amber-200 text-amber-800"

  return (
    <Link
      href={`/guides/${metadata.slug}`}
      data-module="guides_landing_card"
      className="group flex flex-col ds-glass rounded-2xl hover:-translate-y-0.5 transition-base overflow-hidden"
    >
      {/* Tinted header with blade illustration */}
      <div className={`relative h-36 border-b border-white/40 overflow-hidden ${headerGradient}`}>
        <span
          className={`absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full backdrop-blur-xs border text-[10.5px] font-bold uppercase tracking-wider ${pillStyles}`}
        >
          {isCool ? <Snowflake className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
          {isCool ? "Cool" : "Warm"}
        </span>
        <div className="absolute inset-0 pt-4">
          {Blade ? <Blade color={bladeColor} /> : null}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col px-5 pt-4 pb-2 gap-2">
        <h3 className="text-lg font-bold text-gray-900 leading-tight tracking-tight">
          {metadata.displayName}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed line-clamp-2 min-h-[2.6rem]">
          {metadata.shortDescription}
        </p>
      </div>

      {/* Meta strip — hairline separated */}
      <div className="mx-5 mt-2 py-3 border-t border-white/45 grid grid-cols-2 gap-1.5">
        <Meta label="Zones" value={metadata.zones} />
        <Meta label="Maintenance" value={maintenanceLabel(grassData.maintenanceLevel)} />
      </div>

      {/* Footer CTA */}
      <div className="mx-5 mb-5">
        <span className="w-full inline-flex items-center justify-between gap-2 px-4 py-2.5 bg-green-600 group-hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-colors group-hover:shadow-lg group-hover:shadow-green-600/25">
          <span>Read the {metadata.displayName} guide</span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </span>
      </div>
    </Link>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-[10.5px] font-semibold tracking-wider uppercase text-gray-500">
        {label}
      </span>
      <span className="text-sm font-semibold text-gray-800">
        {value}
      </span>
    </div>
  )
}
